import { useEffect, useState } from "react";
import { AlertTriangle, CheckCircle2, Search } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Field, SimpleSelect } from "@/components/wizard/Field";
import { money, TaxBadge } from "./common";
import { t } from "@/i18n";

export const ReportAssignDialog = ({ report, onClose, onApplied }) => {
  const [res, setRes] = useState(null);
  const [err, setErr] = useState(null);
  const [period, setPeriod] = useState(report?.period || "");
  const [currency, setCurrency] = useState("USD");
  const [selected, setSelected] = useState([]);
  const [busy, setBusy] = useState(false);
  const [showUnmatched, setShowUnmatched] = useState(false);

  useEffect(() => {
    if (!report) return;
    setRes(null); setErr(null); setPeriod(report.period || ""); setSelected([]);
    api.post(`/payments/reports/${report.id}/analyze`).then((r) => { setRes(r.data); setSelected(r.data.users.filter((u) => u.gross > 0).map((u) => u.user_id)); }).catch((e) => setErr(errMsg(e)));
  }, [report]);

  const toggle = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const apply = async (force = false) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/payments/reports/${report.id}/apply`, { period, currency, user_ids: selected, force });
      toast.success(t("{n} statements creados", { n: data.created.length })); onApplied(); onClose();
    } catch (e) {
      if (e.response?.status === 409 && window.confirm(t("Este reporte ya fue asignado antes. ¿Crear statements de nuevo?"))) return apply(true);
      toast.error(errMsg(e));
    } finally { setBusy(false); }
  };
  const total = res ? res.users.filter((u) => selected.includes(u.user_id)).reduce((a, u) => a + u.gross, 0) : 0;

  return (
    <Dialog open={!!report} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="report-assign-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Search className="h-4 w-4 text-primary" /> {t("Asignar reporte a artistas")}</DialogTitle>
          <DialogDescription>{t("Se buscan los UPC / ISRC del archivo en el catálogo. El neto de cada línea se toma como bruto del artista y se le descuenta su retención.")}</DialogDescription>
        </DialogHeader>
        {err && <p className="text-sm text-destructive" data-testid="assign-error">{err}</p>}
        {!res && !err && <p className="text-sm text-muted-foreground" data-testid="assign-loading">{t("Leyendo el archivo…")}</p>}
        {res && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
              <div className="panel p-3"><p className="eyebrow">{t("Líneas leídas")}</p><p className="font-semibold text-lg" data-testid="assign-total-lines">{res.total_lines}</p></div>
              <div className="panel p-3"><p className="eyebrow">{t("Encontradas en el panel")}</p><p className="font-semibold text-lg text-emerald-400" data-testid="assign-matched">{res.matched_lines} · {money(res.matched_total, currency)}</p></div>
              <div className="panel p-3"><p className="eyebrow">{t("Sin coincidencia")}</p><p className={`font-semibold text-lg ${res.unmatched_lines ? "text-amber-300" : ""}`} data-testid="assign-unmatched">{res.unmatched_lines} · {money(res.unmatched_total, currency)}</p></div>
              <div className="panel p-3"><p className="eyebrow">{t("A asignar")}</p><p className="font-semibold text-lg" data-testid="assign-selected-total">{money(total, currency)}</p></div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Periodo"><Input placeholder="2026-03" value={period} onChange={(e) => setPeriod(e.target.value)} data-testid="assign-period" /></Field>
              <Field label="Moneda"><SimpleSelect value={currency} onChange={setCurrency} options={["USD", "MXN", "EUR"]} testId="assign-currency" /></Field>
            </div>
            <Table data-testid="assign-users-table">
              <TableHeader><TableRow><TableHead /><TableHead>{t("Artista / cuenta")}</TableHead><TableHead>{t("Formulario")}</TableHead><TableHead className="text-right">{t("Líneas")}</TableHead><TableHead className="text-right">{t("Bruto")}</TableHead><TableHead className="text-right">{t("Retención")}</TableHead><TableHead className="text-right">{t("Neto a pagar")}</TableHead></TableRow></TableHeader>
              <TableBody>
                {res.users.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-sm text-muted-foreground py-6">{t("Ningún UPC/ISRC del archivo coincide con lanzamientos del panel.")}</TableCell></TableRow>}
                {res.users.map((u) => (
                  <TableRow key={u.user_id} data-testid={`assign-user-${u.user_id}`}>
                    <TableCell><Checkbox checked={selected.includes(u.user_id)} onCheckedChange={() => toggle(u.user_id)} data-testid={`assign-check-${u.user_id}`} /></TableCell>
                    <TableCell><p className="font-medium">{u.user_name}</p><p className="text-xs text-muted-foreground">{u.email}{u.country ? ` · ${u.country}` : ""}</p></TableCell>
                    <TableCell>{u.has_profile ? <TaxBadge status={u.tax_form_status} /> : <span className="text-xs text-amber-300 inline-flex items-center gap-1"><AlertTriangle className="h-3 w-3" /> {t("Sin datos de pago (30%)")}</span>}</TableCell>
                    <TableCell className="text-right tabular-nums">{u.line_count}</TableCell>
                    <TableCell className="text-right tabular-nums">{money(u.gross, currency)}</TableCell>
                    <TableCell className="text-right tabular-nums text-muted-foreground">{money(u.withholding, currency)} <span className="text-[10px]">({u.withholding_pct}%)</span></TableCell>
                    <TableCell className="text-right tabular-nums font-semibold" data-testid={`assign-net-${u.user_id}`}>{money(u.net, currency)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {res.unmatched_lines > 0 && (
              <div className="space-y-2">
                <button type="button" className="text-xs text-primary hover:underline" onClick={() => setShowUnmatched((s) => !s)} data-testid="assign-toggle-unmatched">{showUnmatched ? t("Ocultar líneas sin coincidencia") : t("Ver líneas sin coincidencia ({n})", { n: res.unmatched_lines })}</button>
                {showUnmatched && (
                  <div className="max-h-48 overflow-auto rounded-md border border-border text-xs font-mono p-2 space-y-1" data-testid="assign-unmatched-list">
                    {res.unmatched.map((l, i) => <p key={i} className="truncate text-muted-foreground">{l.upc || ""} {l.isrc || ""} · {money(l.amount, currency)} · {l.raw}</p>)}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>{t("Cancelar")}</Button>
          <Button onClick={() => apply(false)} disabled={busy || !res || !selected.length || !period.trim()} className="gap-2" data-testid="assign-submit"><CheckCircle2 className="h-4 w-4" /> {t("Crear statements ({n})", { n: selected.length })}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
